import type { TemplateType } from '@contracts';

export type TemplateKey =
  | 'FLIGHTS_LISTING'
  | 'FLIGHTS_LISTING_SPLIT'
  | 'FLIGHTS_LISTING_SIMPLE'
  | 'FLIGHTS_REVIEW'
  | 'FLIGHTS_REVIEW_INTERSTITIAL'
  | 'FLIGHTS_REVIEW_TRAVELLERS'
  | 'FLIGHTS_THANKYOU'
  | 'ROUTE_OPTIONS'
  | 'FARE_CALENDAR'
  | 'HOTEL_DETAILS'
  | 'HOTEL_ROOM_SELECTION'
  | 'HOTEL_REVIEW'
  | 'HOTEL_REVIEW_ERROR'
  | 'HOTEL_COUPON_UPDATE'
  | 'HOTEL_TRAVELLER_DETAIL'
  | 'HOTEL_GST_DETAIL'
  | 'HOTEL_PAN_DETAIL'
  | 'HOTEL_CHECKOUT'
  | 'HOTELS_THANKYOU'
  | 'ITINERARY'
  | 'TRIP_GUIDE'
  | 'HOTELS_LISTING';

export const TEMPLATE_TYPES: Record<TemplateKey, TemplateType> = {
  FLIGHTS_LISTING: 'flights-listing',
  FLIGHTS_LISTING_SPLIT: 'flights-listing-split',
  FLIGHTS_LISTING_SIMPLE: 'flights-listing-simple',
  FLIGHTS_REVIEW: 'flights-review',
  FLIGHTS_REVIEW_INTERSTITIAL: 'flights-review-interstitial',
  FLIGHTS_REVIEW_TRAVELLERS: 'flights-review-travellers',
  FLIGHTS_THANKYOU: 'flights-thankyou',
  ROUTE_OPTIONS: 'route-options',
  FARE_CALENDAR: 'fare-calendar',
  HOTEL_DETAILS: 'hotels-details',
  HOTEL_ROOM_SELECTION: 'hotels-room-selection',
  HOTEL_REVIEW: 'hotels-review',
  HOTEL_REVIEW_ERROR: 'hotels-review-error',
  HOTEL_COUPON_UPDATE: 'hotels-coupon-update',
  HOTEL_TRAVELLER_DETAIL: 'hotels-traveller-detail',
  HOTEL_GST_DETAIL: 'hotels-gst-detail',
  HOTEL_PAN_DETAIL: 'hotels-pan-detail',
  HOTEL_CHECKOUT: 'hotels-checkout',
  HOTELS_THANKYOU: 'hotels-thankyou',
  ITINERARY: 'itinerary-plan',
  TRIP_GUIDE: 'trip-guide',
  HOTELS_LISTING: 'hotels-listing',
};

/**
 * Response types that are NOT templates — they must never become activeTemplate.
 * They trigger actions (apply coupon, show QnA gallery) but do not represent
 * a right-panel template.
 *
 * - hotels-coupon-update: Updates coupon/pricing in booking review slice
 * - hotels-qna: QnA responses with intents (e.g., HOTEL_MEDIA) — opens popup, not a template
 */
/**
 * Response types that are NOT templates — they must never become activeTemplate.
 * They trigger actions (apply coupon, update travellers, update GST, redirect to payment, show QnA gallery)
 * but do not represent a right-panel template.
 *
 * - hotels-coupon-update: Updates coupon/pricing in booking review slice
 * - hotels-traveller-detail: Updates traveller list in booking review slice
 * - hotels-gst-detail: Updates GST details in booking review slice
 * - hotels-pan-detail: Updates PAN details in booking review slice
 * - hotels-checkout: Redirects to payment page
 * - hotels-qna: QnA responses with intents (e.g., HOTEL_MEDIA) — opens popup, not a template
 */
export const DATA_ONLY_TEMPLATE_TYPES = new Set<TemplateType>([
  'hotels-coupon-update',
  'hotels-traveller-detail',
  'hotels-gst-detail',
  'hotels-pan-detail',
  'hotels-checkout',
  'hotels-qna',
]);

/**
 * Template types that are skipped during live streaming but ARE renderable
 * in the template pane (for chat history / card clicks).
 *
 * During live stream: template switch is skipped, owning slice handles data.
 * During history load / card click: template renders normally in TemplatePane.
 */
export const STREAM_SKIP_TEMPLATE_TYPES = new Set<TemplateType>(['hotels-review-error']);

/**
 * Templates triggered by a search query (flight/hotel/itinerary search).
 * Used to pick the right loading animation (LottieLoader vs LottieLoaderAlt).
 */
export const SEARCH_TEMPLATE_TYPES = new Set<TemplateType>([
  TEMPLATE_TYPES.FLIGHTS_LISTING,
  TEMPLATE_TYPES.FLIGHTS_LISTING_SPLIT,
  TEMPLATE_TYPES.FLIGHTS_LISTING_SIMPLE,
  TEMPLATE_TYPES.HOTELS_LISTING,
  TEMPLATE_TYPES.ITINERARY,
  TEMPLATE_TYPES.TRIP_GUIDE,
  TEMPLATE_TYPES.ROUTE_OPTIONS,
]);
